<template>
  <div
    class="wrapper"
    :class="{ 'wrapper--guest': !showNav, 'wrapper--ios-cordova': isIosCordova }"
  >
    <TopBar v-if="showNav" @toggle-menu="toggleMenu" />
    <MenuBar
      v-if="showNav"
      :opened="menuOpened"
      :window-width="windowWidth"
      @close="closeMenu"
    />

    <main
      id="router__view"
      class="router__view"
      :class="{ 'router__view--guest': !showNav }"
    >
      <RouterView />
    </main>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import MenuBar from "@/components/common/MenuBar.vue";
import TopBar from "@/components/common/TopBar.vue";
import { useViewerStore } from "@/stores/viewer";

const route = useRoute();
const viewerStore = useViewerStore();

function getWindowWidth() {
  return typeof window !== "undefined" ? window.innerWidth : 1280;
}

const windowWidth = ref(getWindowWidth());
const menuOpened = ref(getWindowWidth() > 767);
const guestPaths = ["/login", "/no-connection", "/privacy", "/privacy-policy"];
const showNav = computed(() => !guestPaths.includes(route.path));
const isIosCordova =
  import.meta.env.VITE_APP_PLATFORM === "cordova" &&
  import.meta.env.VITE_CORDOVA_PLATFORM === "ios";

function onResize() {
  windowWidth.value = window.innerWidth;
  menuOpened.value = windowWidth.value > 767;
}

function toggleMenu() {
  if (windowWidth.value > 767) return;
  menuOpened.value = !menuOpened.value;
}

function closeMenu() {
  if (windowWidth.value > 767) return;
  menuOpened.value = false;
}

watch(
  () => route.fullPath,
  () => {
    if (windowWidth.value <= 767) {
      menuOpened.value = false;
    }
  }
);

onMounted(() => {
  viewerStore.initFromStorage();
  if (typeof window !== "undefined") {
    window.addEventListener("resize", onResize);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", onResize);
  }
});
</script>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 50px;
  padding-bottom: 60px;
  font-size: 16px;
  font-family: Roboto, sans-serif;
  background: var(--wrapper-bg);
  color: var(--text-color);
}

.wrapper--guest {
  padding-top: 0;
  padding-bottom: 0;
}

.wrapper--ios-cordova {
  padding-bottom: 82px;
}

@media screen and (min-width: 768px) {
  .wrapper {
    padding-top: 56px;
    padding-bottom: 0;
  }
}

@media screen and (max-width: 767px) {
  .wrapper {
    padding-top: 56px;
  }

  .wrapper--guest {
    padding-top: 0;
  }
}

@media screen and (min-width: 768px) {
  .wrapper--guest {
    padding-top: 0;
    padding-bottom: 0;
  }
}

.router__view {
  width: 100%;
  margin-left: 0;
  padding: 12px;
}

.router__view--guest {
  padding: 0;
}

@media screen and (min-width: 768px) and (max-width: 1199px) {
  .router__view {
    width: calc(100vw - 260px);
    margin-left: 260px;
    padding: 16px;
  }
}

@media screen and (min-width: 1200px) {
  .router__view {
    width: min(1089px, calc(100vw - 15% - 260px - 10% - 24px));
    margin-left: calc(15% + 260px);
    padding: 20px 0;
  }
}

@media screen and (min-width: 768px) {
  .router__view--guest {
    width: 100%;
    margin-left: 0;
    padding: 0;
  }
}

@media screen and (max-width: 767px) {
  .router__view {
    padding: 0;
  }
}
</style>
